/**
 * A primeira tela: o mês de cada congregação, e o que precisa de atenção.
 *
 * A ordem da tela é a ordem da urgência, e não a ordem do banco:
 *
 * 1. **As pendências primeiro.** Lançamento esperando o mês reabrir é dinheiro lançado que
 *    não está em livro nenhum; notebook calado há uma semana pode ser um computador com
 *    defeito. As duas coisas o pastor precisa ver antes de olhar número nenhum.
 * 2. **Depois cada congregação**, com o que entrou, o que saiu e em que situação o mês está.
 *
 * Quem vê uma congregação só (o pastor local) recebe a lista com uma linha, e nem sabe que
 * as outras existem: o corte é feito no servidor, e não aqui. Esta tela nunca esconde nada
 * que o servidor mandou; se ela escondesse, a permissão estaria na tela, que é justamente o
 * lugar onde permissão não pode morar.
 */
import { useEffect, useState, type JSX } from 'react'
import { AtualizadoAs, useAtualizarSozinho } from '../componentes/AtualizadoAs'
import { ehSessaoTerminada, ErroDoServidor, servidor, type Eu, type Inicio as DadosDoInicio } from '../api'
import { BaixarOPrograma } from '../componentes/BaixarOPrograma'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { competenciaPorExtenso, emReais, mesAnterior, mesSeguinte, NOME_DA_SITUACAO, quandoFoi } from '../dinheiro'
import { enderecoDaCongregacao, enderecoDoCampo, enderecoDoInicio, irPara } from '../rotas'

/**
 * Entrou com um código de emergência: o que isso gastou, e quando é hora de um celular novo.
 *
 * Pedido da auditora, de 17/10/2026: a tela não dizia que o código foi gasto, nem que o
 * aplicativo do celular continua valendo, nem quando é preciso cadastrar outro.
 */
function AvisoDoCodigoDeEmergencia({ sobram }: { sobram: number }): JSX.Element {
  return (
    <section className="explicacao atencao aviso-de-emergencia" role="status">
      <p>
        <strong>Você entrou com um código de emergência.</strong> Ele foi gasto e não vale mais.{' '}
        {sobram === 0
          ? 'Não sobrou nenhum dos dez.'
          : `Sobram ${sobram} ${sobram === 1 ? 'código' : 'códigos'} dos dez.`}
      </p>
      <p>
        O código não desliga o segundo fator nem troca o celular: ele abre <strong>uma</strong>{' '}
        entrada. Se o celular está com você, use o número do aplicativo da próxima vez.
      </p>
      <p>
        <strong>Perdeu ou trocou de celular?</strong> Cadastre o novo agora, em{' '}
        <button className="como-link" onClick={() => irPara('/segundo-fator')}>Segundo fator</button>,
        no botão "Trocar de celular". O antigo para de valer e você ganha dez códigos novos. Sem
        celular e sem códigos, só o administrador máximo do Campo consegue desligar o segundo fator
        da sua conta.
      </p>
    </section>
  )
}

export function Inicio(
  { eu, diaDoEndereco, aoSair }: { eu: Eu; diaDoEndereco: string | null; aoSair: () => void }
): JSX.Element {
  const [competencia, setCompetencia] = useState<string | undefined>(undefined)
  const [dados, setDados] = useState<DadosDoInicio | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const atualizacao = useAtualizarSozinho()
  const { rodada, marcarAtualizado } = atualizacao

  useEffect(() => {
    let vale = true
    setErro(null)

    servidor
      .inicio(competencia, diaDoEndereco)
      .then((recebido) => {
        if (!vale) return
        setDados(recebido)
        marcarAtualizado()
      })
      .catch((falha: unknown) => {
        if (!vale) return
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })

    return () => {
      vale = false
    }
  }, [competencia, diaDoEndereco, aoSair, rodada, marcarAtualizado])

  const mesMostrado = dados?.competencia ?? competencia ?? null

  return (
    <>
      <BarraDeCima eu={eu} aoSair={aoSair} />

      <main className="folha">
        <h1>Início</h1>

        {mesMostrado !== null && (
          <nav className="navegar-mes">
            <button aria-label="Mês anterior" onClick={() => setCompetencia(mesAnterior(mesMostrado))}>
              ◀
            </button>
            <strong>{competenciaPorExtenso(mesMostrado)}</strong>
            <button aria-label="Mês seguinte" onClick={() => setCompetencia(mesSeguinte(mesMostrado))}>
              ▶
            </button>
            {competencia !== undefined && (
              // Só aparece quando a pessoa saiu do mês de hoje. É o caminho de volta.
              <button className="voltar" onClick={() => setCompetencia(undefined)}>
                mês atual
              </button>
            )}
          </nav>
        )}

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            <AtualizadoAs atualizacao={atualizacao} />
            {eu.entrou_com_codigo_de_emergencia && (
              <AvisoDoCodigoDeEmergencia sobram={eu.entrou_com_codigo_de_emergencia.sobram} />
            )}
            <CartaoDeHoje
              hoje={dados.hoje}
              diaPedido={diaDoEndereco}
              mesDaLista={dados.competencia}
            />

            <Pendencias dados={dados} />

            {/*
              O caminho para a administração, só para quem cuida do cadastro de todo o Campo.
              Quem decide de verdade é o servidor, em cada rota; aqui só se evita oferecer
              uma porta que vai dar recusa na cara da pessoa.
            */}
            {eu.administra_o_campo && (
              <p className="totais">
                <button className="voltar" onClick={() => irPara('/notebooks')}>
                  Notebooks, Pessoas e Auditoria ▶
                </button>
              </p>
            )}
            {!eu.administra_o_campo && eu.ve_a_auditoria && (
              <p className="totais">
                <button className="voltar" onClick={() => irPara('/auditoria')}>
                  Auditoria ▶
                </button>
              </p>
            )}

            {/*
              O caminho para a tela do Campo, só para quem alcança o Campo inteiro. Para o
              pastor de uma congregação ele não aparece, e o servidor recusaria de todo jeito:
              a permissão mora lá, e aqui só se evita oferecer uma porta fechada.
            */}
            {eu.ve_o_campo_inteiro && (
              <p className="totais">
                <button className="voltar" onClick={() => irPara(enderecoDoCampo(competencia))}>
                  Abrir o Campo: Movimento e Fluxo do Caixa ▶
                </button>
              </p>
            )}

            {dados.congregacoes.length === 0 ? (
              <p className="recado nada">
                Você ainda não tem permissão para ver nenhuma congregação. Fale com o pastor do Campo.
              </p>
            ) : (
              <>
                <ul className="congregacoes">
                  {dados.congregacoes.map((linha) => (
                    <li key={linha.congregacao.id}>
                      {/*
                        A linha inteira é o caminho para a folha daquela congregação. Botão e
                        não link porque a navegação é por `pushState`, mas com cara de linha
                        clicável: quem usa isto no celular acerta o dedo na linha, não num
                        alvo pequeno no canto.
                      */}
                      <button
                        className="abrir"
                        onClick={() => irPara(enderecoDaCongregacao(linha.congregacao.id, competencia))}
                        aria-label={`Abrir as folhas de ${linha.congregacao.nome}`}
                      />
                      <span className="nome">
                        {linha.congregacao.nome}
                        {/* O aviso de sede só quando o nome já não disser isso sozinho. */}
                        {linha.congregacao.e_sede && !/sede/i.test(linha.congregacao.nome) && (
                          <small>sede</small>
                        )}
                        <span className={`situacao ${linha.situacao}`}>
                          {NOME_DA_SITUACAO[linha.situacao] ?? linha.situacao}
                        </span>
                      </span>
                      <span className="numeros">
                        <div>
                          entrou
                          <strong>{emReais(linha.entradas)}</strong>
                        </div>
                        <div className="saiu">
                          saiu do caixa
                          <strong>{emReais(linha.saidas)}</strong>
                        </div>
                      </span>
                    </li>
                  ))}
                </ul>

                {dados.congregacoes.length > 1 && (
                  <p className="totais">
                    <span>
                      entrou no Campo <strong>{emReais(dados.total.entradas)}</strong>
                    </span>
                    <span>
                      saiu dos caixas <strong>{emReais(dados.total.saidas)}</strong>
                    </span>
                  </p>
                )}
              </>
            )}
          </>
        )}

        {/*
          O convite do segundo fator, no fim e não no alto.

          No alto ele atrapalharia quem abriu o painel para ver um número, que é a razão de
          alguém abrir o painel. No fim, ele aparece para quem rolou a tela inteira, e
          continua aparecendo todo dia até a pessoa ligar.
        */}
        {!eu.tem_segundo_fator ? (
          <button className="cartao caminho so-tela" onClick={() => irPara('/segundo-fator')}>
            <span className="titulo">Esta conta ainda não tem segundo fator</span>
            <span className="detalhe">
              Hoje, quem souber a sua senha entra aqui. Ligar leva três passos, uma vez só.
              Toque para ligar.
            </span>
          </button>
        ) : (
          <p className="explicacao miudo so-tela">
            O segundo fator está ligado nesta conta.{' '}
            <button className="como-link" type="button" onClick={() => irPara('/segundo-fator')}>
              Trocar de celular
            </button>
          </p>
        )}

        {/* Ao lado do segundo fator, que é a outra coisa da conta da própria pessoa. */}
        <p className="explicacao miudo so-tela">
          <button className="como-link" type="button" onClick={() => irPara('/minha-senha')}>
            Trocar a minha senha
          </button>
        </p>

        {/*
          O botão de baixar o programa, no pé e para qualquer conta.

          Nenhuma permissão é olhada: baixar o instalador não é decisão de dinheiro nem de
          sistema. Aqui ele serve ao segundo computador da tesouraria e ao notebook que foi
          formatado, e quem precisa dele antes de entrar o encontra na tela de entrar.
        */}
        <BaixarOPrograma />

        {/*
          IR PARA O TREINAMENTO.

          Só aparece quando existe treinamento para este Campo, e quando esta sessão ainda não
          está nele. Dentro do treinamento quem leva de volta é a faixa amarela, que está em
          toda tela, e não este botão, que está numa só.

          Nenhuma permissão é olhada, e isso é escolhido: no treinamento nada vale, e qualquer
          pessoa da equipe precisa poder ensaiar o trabalho dela.
        */}
        {eu.tem_treinamento && !eu.treinamento && (
          <p className="explicacao so-tela">
            <button
              className="como-link"
              type="button"
              onClick={() => {
                void servidor.irParaOTreinamento().then(() => window.location.reload())
              }}
            >
              Ir para o treinamento
            </button>
            {' '}· lá nada vale, e dá para errar à vontade.
          </p>
        )}

        {/*
          E, DENTRO do treinamento, o botão de recomeçar.

          "Por qualquer usuário, a qualquer hora", que foi o pedido. Sem senha de novo, sem
          palavra digitada e sem exigir administrador máximo, ao contrário das ações que mexem no
          acesso do Campo: ali não há nada a guardar.

          O aviso de que vale para a equipe inteira fica ANTES do botão, e não depois: quem lê de
          cima para baixo encontra o que acontece antes de decidir.
        */}
        {eu.treinamento && (
          <p className="explicacao atencao so-tela">
            Recomeçar o treinamento apaga tudo o que foi lançado aqui e refaz do cadastro de
            agora, <strong>para a equipe inteira</strong>.{' '}
            <button
              className="como-link"
              type="button"
              onClick={() => {
                void servidor.recomecarOTreinamento().then(() => window.location.reload())
              }}
            >
              Recomeçar o treinamento
            </button>
          </p>
        )}

        {/*
          O caminho para a tela de Sobre, no pé da primeira tela.

          É onde todo programa põe, e é o que alguém procura quando quer saber qual versão está
          no ar. Miúdo de propósito: ninguém abre o painel para ler isto.
        */}
        <p className="explicacao miudo so-tela">
          <button className="como-link" type="button" onClick={() => irPara('/sobre')}>
            Sobre este sistema
          </button>
        </p>
      </main>
    </>
  )
}

/**
 * O que precisa de atenção, em destaque.
 *
 * Quando não há nada, a tela diz que não há nada. Espaço vazio deixa a dúvida de se a
 * informação não existe ou não carregou.
 */
/**
 * O cartão "Hoje": quanto dinheiro vivo entrou hoje, em cada congregação.
 *
 * **A única tela do painel que não existe no programa do notebook**, e a exceção foi aberta pelo
 * Cristiano em 12/10/2026. O porquê está por extenso em `src/painel/hoje.ts`, do lado do
 * servidor; em uma frase: a pergunta que o pastor faz toda noite de culto é "quanto entrou em
 * dinheiro hoje", e no notebook essa resposta não existe pronta, porque a folha é do mês e a de
 * dízimos não soma por forma de pagamento.
 *
 * Ela não é folha nem documento: é o atalho para a folha de verdade, e abre nela, no dia escolhido.
 *
 * **O dinheiro em destaque é o que precisa ser contado**, e é por isso que ele vem primeiro e
 * grande: espécie mais moeda das ofertas, mais os dízimos em espécie. O PIX e o cartão vão
 * embaixo, em letra menor, porque já estão na conta e ninguém vai contar.
 *
 * ## As setas e o campo de data, pedido do Cristiano de 13/10/2026
 *
 * O cartão nasce em hoje e anda para qualquer dia que já passou: ontem, o domingo passado. O
 * título vira "HOJE, 13/10/2026" ou "DOMINGO, 11/10/2026", e quem decide qual dos dois é o
 * SERVIDOR, que manda `data_de_hoje` junto. Celular com a data adiantada escreveria "Hoje" num
 * domingo que já passou, e é o mesmo motivo do campo `hoje` da tela de Congregação.
 *
 * O dia mora no ENDEREÇO, e não na memória da tela: recarregar a página no celular não pode
 * devolver o pastor para hoje depois de ele ter andado seis dias para trás, e o link que ele
 * manda à tesoureira precisa abrir no mesmo dia.
 */
function CartaoDeHoje(
  { hoje, diaPedido, mesDaLista }:
  { hoje: DadosDoInicio['hoje']; diaPedido: string | null; mesDaLista: string }
): JSX.Element {
  const ehHoje = hoje.data === hoje.data_de_hoje
  /** Pediu um dia e recebeu outro: o servidor não entendeu o que estava no endereço. */
  const naoEntendeu = diaPedido !== null && diaPedido !== hoje.data

  /** Quem não lançou nada nem tem culto na agenda não precisa aparecer na lista do dia. */
  const comAlgumaCoisa = hoje.congregacoes.filter(
    (l) => l.especie !== 0 || l.pix !== 0 || l.cartao !== 0 || l.tem_culto_na_agenda
  )

  return (
    <section className="cartao-de-hoje">
      <h2>{ehHoje ? 'Hoje' : hoje.dia_da_semana}, {hoje.data_por_extenso}</h2>

      {/* O padrão é o da navegação de mês das outras telas, com a mesma classe. */}
      <nav className="navegar-mes navegar-dia">
        <button
          aria-label="Dia anterior"
          onClick={() => irPara(enderecoDoInicio(hoje.dia_anterior))}
        >
          ◀
        </button>
        <input
          type="date"
          aria-label="Ver outro dia"
          value={hoje.data}
          /* Não oferece o futuro: dia que ainda não chegou não tem o que mostrar. */
          max={hoje.data_de_hoje}
          onChange={(e) => { if (e.target.value) irPara(enderecoDoInicio(e.target.value)) }}
        />
        <button
          aria-label="Dia seguinte"
          disabled={hoje.dia_seguinte === null}
          onClick={() => irPara(enderecoDoInicio(hoje.dia_seguinte))}
        >
          ▶
        </button>
        {!ehHoje && (
          <button className="voltar" onClick={() => irPara(enderecoDoInicio())}>hoje</button>
        )}
      </nav>

      {naoEntendeu && (
        <p className="lapis">O dia pedido no endereço não foi entendido. Esta é a folha de hoje.</p>
      )}

      {hoje.competencia !== mesDaLista && (
        <p className="lapis">
          Este dia é de {competenciaPorExtenso(hoje.competencia)}, e a lista de congregações
          abaixo é de {competenciaPorExtenso(mesDaLista)}.
        </p>
      )}

      {!hoje.teve_movimento ? (
        <>
          <p className="nada-hoje">
            {ehHoje ? 'Nada lançado hoje ainda.' : 'Nada lançado neste dia.'}
          </p>
          <p className="lapis">
            {comAlgumaCoisa.length === 0
              ? ehHoje
                ? 'Não há culto na agenda de hoje.'
                : 'Não houve culto na agenda deste dia.'
              : ehHoje
                ? 'A tesouraria lança depois do culto, e o número aparece aqui na sincronização seguinte.'
                : 'Houve culto neste dia e nada foi lançado. Veja se o notebook daquela congregação enviou.'}
          </p>
        </>
      ) : (
        <>
          <p className="em-dinheiro">
            <strong>{emReais(hoje.total.especie)}</strong> em dinheiro
          </p>
          {(hoje.total.pix !== 0 || hoje.total.cartao !== 0) && (
            <p className="a-parte">
              PIX {emReais(hoje.total.pix)} · cartão {emReais(hoje.total.cartao)}
            </p>
          )}
        </>
      )}

      {comAlgumaCoisa.length > 0 && (
        <ul className="de-hoje">
          {comAlgumaCoisa.map((l) => (
            <li key={l.congregacao.id}>
              {/*
                A linha inteira abre a folha de ofertas daquela congregação no mês do DIA
                MOSTRADO, e a folha rola até o culto daquele dia. É o que faz o cartão ser
                atalho, e não um número solto que obriga a procurar.
              */}
              <button
                className="abrir"
                onClick={() =>
                  irPara(enderecoDaCongregacao(l.congregacao.id, hoje.competencia, hoje.data))}
                aria-label={`Abrir a folha de ${l.congregacao.nome} no culto deste dia`}
              />
              <span className="nome">{l.congregacao.nome}</span>
              <span className="valores">
                <strong>{emReais(l.especie)}</strong>
                {(l.pix !== 0 || l.cartao !== 0) && (
                  <small>PIX {emReais(l.pix)} · cartão {emReais(l.cartao)}</small>
                )}
                {l.especie === 0 && l.pix === 0 && l.cartao === 0 && (
                  <small>ainda não lançou</small>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="lapis explicacao-do-dinheiro">
        Dinheiro é a espécie e a moeda das ofertas, mais os dízimos e votos lançados em espécie.
        A oferta de culto com destino de departamento entra aqui, porque ela também passou pela
        mão da tesouraria {ehHoje ? 'hoje' : 'naquele dia'}, e por isso este número pode ser maior
        do que a entrada do mês daquela congregação.
      </p>
    </section>
  )
}

function Pendencias({ dados }: { dados: DadosDoInicio }): JSX.Element {
  const espera = dados.pendencias.aguardando_reabertura
  const calados = dados.pendencias.notebooks_sem_enviar
  const saidasEsperando = dados.pendencias.saidas_esperando
  const corrigidas = dados.pendencias.saidas_corrigidas_para_voce ?? []

  if (espera.lancamentos === 0 && calados.length === 0 && saidasEsperando.quantas === 0 && corrigidas.length === 0) {
    return (
      <p className="recado nada">
        Nada pendente. Nenhuma saída esperando decisão, e todos os notebooks enviaram na
        última semana.
      </p>
    )
  }

  return (
    <section className="pendencias">
      {/*
        O AVISO A QUEM TINHA APROVADO (0.3.24): a saída que você aprovou foi corrigida por outra
        pessoa no notebook e voltou a aguardar. Vem primeiro, porque é seu: a decisão foi sua.
        Some quando alguém decide de novo.
      */}
      {corrigidas.map((c) => (
        <button key={c.id} className="cartao caminho aviso-de-correcao" onClick={() => irPara('/saidas')}>
          <span className="titulo">Uma saída que você aprovou foi corrigida</span>
          <span className="detalhe">
            A saída "{c.descricao}" ({emReais(c.valor)}{c.congregacao ? `, ${c.congregacao}` : ''}) que você aprovou
            foi corrigida por {c.corrigida_por} e precisa de nova decisão. Toque para ver.
          </span>
        </button>
      ))}

      {saidasEsperando.quantas > 0 && (
        /*
          O cartão das saídas esperando decisão, da etapa 3 da parte 5.

          Ele vem ANTES do de lançamento esperando o mês reabrir, e isso é escolha: saída
          pendente é dinheiro que ainda não andou, e alguém do outro lado está esperando para
          pagar. Lançamento preso já aconteceu no mundo, e o que falta é o livro acertar.

          O número de quantas estão sem o comprovante exigido entra aqui, no detalhe, e não só
          na lista: quem olha a primeira tela e não abre a lista continua sabendo que existe
          coisa que vai segurar o fechamento do mês.
        */
        <button className="cartao caminho" onClick={() => irPara('/saidas')}>
          <span className="titulo">
            {saidasEsperando.quantas === 1
              ? '1 saída esperando decisão'
              : `${saidasEsperando.quantas} saídas esperando decisão`}
          </span>
          <span className="detalhe">
            {emReais(saidasEsperando.soma)} parado, sem decisão.
            {saidasEsperando.sem_o_comprovante_exigido > 0 && (
              <>
                {' '}
                {saidasEsperando.sem_o_comprovante_exigido === 1
                  ? '1 delas está sem o comprovante que a categoria exige'
                  : `${saidasEsperando.sem_o_comprovante_exigido} delas estão sem o comprovante que a categoria exige`}
                , e isso segura o fechamento do mês.
              </>
            )}{' '}
            Toque para ver.
          </span>
        </button>
      )}

      {espera.lancamentos > 0 && (
        /*
          O cartão inteiro é o caminho para a tela de Aguardando reabertura. Quem lê que há
          lançamento preso quer, no mesmo segundo, ver qual é.
        */
        <button className="cartao caminho" onClick={() => irPara('/aguardando')}>
          <span className="titulo">
            {espera.lancamentos === 1
              ? '1 lançamento esperando o mês reabrir'
              : `${espera.lancamentos} lançamentos esperando o mês reabrir`}
          </span>
          <span className="detalhe">
            {espera.grupos === 1 ? 'em 1 mês fechado' : `em ${espera.grupos} meses fechados`}. Eles estão
            guardados e entram assim que o mês for reaberto. Toque para ver e reabrir.
          </span>
        </button>
      )}

      {calados.map((notebook) => (
        <p className="cartao" key={notebook.id}>
          <span className="titulo">{notebook.nome} não envia há mais de uma semana</span>
          <span className="detalhe">
            {notebook.congregacao ?? 'congregação não identificada'} · {quandoFoi(notebook.ultimo_envio)}
          </span>
        </p>
      ))}
    </section>
  )
}
